import { arrayRemove, arrayUnion, getDoc, serverTimestamp, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { createNumberedDoc } from '@/lib/firestore/numbering';
import { agentDoc } from '@/lib/firestore/refs';
import { logAudit } from '@/lib/records/audit';
import { initials } from '@shared/format';
import type { Agent, AgentStatus, AiProviderId, PrincipalRef } from '@shared/types';

export const WIZARD_STEPS = ['profile', 'provisioning', 'skills', 'tasks', 'review'] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

export const DEFAULT_HANDOFF = 'Escalate to a human when confidence is below the threshold, when the caller asks for a person, or after three failed attempts.';

export async function createDraftAgent(by: PrincipalRef, defaults: { provider: AiProviderId; model: string }): Promise<string> {
  const id = await createNumberedDoc(db, 'AGT', 'agents', () => ({
    name: '', slug: '', avatar: 'AI', role: '', dept: 'IT Operations', status: 'Draft', principalType: 'agent', provider: defaults.provider, model: defaults.model, version: '1.0.0', deployed: null, lastActive: null,
    systemPrompt: '', persona: '', model_params: { temperature: 0.2, max_tokens: 1024, top_p: 1, effort: 'none' }, skills: [], kb_access: [], tools: ['getIncident', 'searchKnowledge', 'addWorkNote'], mcpServers: [],
    scope: { companyIds: '*', categories: [], priorities: ['P1', 'P2', 'P3', 'P4'], groups: [] }, autoTriage: false, tokenBudget: 50000, tokenUsed: 0, budgetPeriod: 'monthly', alertThresholdPct: 80, confidenceThreshold: 70,
    costPerTask: 0, totalCost: 0, tasksCompleted: 0, tasksToday: 0, successRate: 0, avgHandleTime: '—', escalationRate: 0, categories: [], priorities: ['P1', 'P2', 'P3', 'P4'], autoAssignGroups: {}, escalationRules: [], handoffPolicy: DEFAULT_HANDOFF,
    onboardedBy: by, onboardStatus: 'draft', stepsDone: [], createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  }));
  await logAudit(by, 'agent.draft', { type: 'agent', id });
  return id;
}

const slugify = (name: string) => name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

export async function saveAgentStep(id: string, patch: Partial<Agent>, step: WizardStep, by: PrincipalRef): Promise<void> {
  const derived: Partial<Agent> = patch.name ? { slug: slugify(patch.name), avatar: initials(patch.name) } : {};
  const scope: Record<string, unknown> = {};
  if (patch.categories) scope['scope.categories'] = patch.categories;
  if (patch.priorities) scope['scope.priorities'] = patch.priorities;
  await updateDoc(agentDoc(id), { ...patch, ...derived, ...scope, stepsDone: arrayUnion(step), updatedAt: serverTimestamp() } as never);
  await logAudit(by, `agent.wizard.${step}`, { type: 'agent', id }, { after: patch });
}

export function validateForDeploy(a: Agent): string[] {
  const problems: string[] = [];
  if (!a.name?.trim()) problems.push('Give the agent a name.');
  if (!a.role?.trim()) problems.push('Give the agent a role.');
  if (!a.provider || !a.model?.trim()) problems.push('Pick a provider and model.');
  if (!a.systemPrompt?.trim()) problems.push('Write a system prompt.');
  if (!a.tools?.length) problems.push('Grant at least one tool.');
  if (!(a.tokenBudget > 0)) problems.push('Set a monthly token budget above zero.');
  if (!a.categories?.length) problems.push('Pick at least one category the agent handles.');
  return problems;
}

export async function deployAgent(a: Agent, by: PrincipalRef): Promise<void> {
  const problems = validateForDeploy(a);
  if (problems.length) throw new Error(problems.join(' '));
  await updateDoc(agentDoc(a.id), { status: 'Active', onboardStatus: 'complete', deployed: Timestamp.now(), stepsDone: arrayUnion('review'), updatedAt: serverTimestamp() });
  await logAudit(by, 'agent.deploy', { type: 'agent', id: a.id }, { after: { name: a.name, provider: a.provider, model: a.model, tools: a.tools } });
}

export async function setAgentStatus(id: string, status: AgentStatus, by: PrincipalRef): Promise<void> {
  await updateDoc(agentDoc(id), { status, updatedAt: serverTimestamp() });
  await logAudit(by, `agent.status.${status.toLowerCase()}`, { type: 'agent', id });
}

export async function updateAgentConfig(id: string, patch: Partial<Agent>, by: PrincipalRef): Promise<void> {
  await updateDoc(agentDoc(id), { ...patch, updatedAt: serverTimestamp() } as never);
  await logAudit(by, 'agent.config', { type: 'agent', id }, { after: patch });
}

/** Account-team membership keeps the agent's company scope in sync; a global ('*') scope already covers every company. */
export async function addCompanyToScope(agentId: string, companyId: string): Promise<void> {
  const snap = await getDoc(agentDoc(agentId));
  if (!snap.exists() || snap.data().scope.companyIds === '*') return;
  await updateDoc(agentDoc(agentId), { 'scope.companyIds': arrayUnion(companyId) } as never);
}
export async function removeCompanyFromScope(agentId: string, companyId: string): Promise<void> {
  const snap = await getDoc(agentDoc(agentId));
  if (!snap.exists() || snap.data().scope.companyIds === '*') return;
  await updateDoc(agentDoc(agentId), { 'scope.companyIds': arrayRemove(companyId) } as never);
}
